/** The four user types, what each one is for, and where each one navigates. */

export type UserType = 'student' | 'beginner' | 'professional' | 'company';

export const USER_TYPES: UserType[] = ['student', 'beginner', 'professional', 'company'];

export function isUserType(value: unknown): value is UserType {
  return typeof value === 'string' && (USER_TYPES as string[]).includes(value);
}

export interface RoleDefinition {
  id: UserType;
  label: string;
  purpose: string;
  /** The one line that tells someone they picked the right door. */
  promise: string;
  /** Rank ladder, lowest first. Index maps to the thresholds in progress.ts. */
  ranks: [string, string, string, string, string];
}

export const ROLES: Record<UserType, RoleDefinition> = {
  student: {
    id: 'student',
    label: 'Student',
    purpose: 'Learn AI production, build skills and create a portfolio.',
    promise: 'A syllabus, real assignments, and a portfolio that outlives the course.',
    ranks: ['Runner', 'Trainee', 'Assistant', 'Unit Lead', 'Graduate']
  },
  beginner: {
    id: 'beginner',
    label: 'Beginner',
    purpose: 'Explore AI and start a creative journey.',
    promise: 'Your first finished film, faster than you think.',
    ranks: ['Runner', 'Trainee', 'Operator', 'Maker', 'Filmmaker']
  },
  professional: {
    id: 'professional',
    label: 'Professional',
    purpose: 'AI artist, filmmaker, designer or creative professional.',
    promise: 'A public portfolio companies can find, and briefs that come to you.',
    ranks: ['Runner', 'Crew', 'Operator', 'Lead', 'Principal']
  },
  company: {
    id: 'company',
    label: 'Company',
    purpose: 'Looking for AI talent, content production or project services.',
    promise: 'Post a brief, meet vetted AI crews, ship the film.',
    ranks: ['Registered', 'Briefed', 'Listed', 'Partner', 'Flagship']
  }
};

export interface NavItem {
  to: string;
  label: string;
}

/** Section 14 of the spec: navigation follows the role, not the other way round. */
export const NAV: Record<UserType, NavItem[]> = {
  student: [
    { to: '/', label: 'Home' },
    { to: '/academy', label: 'Academy' },
    { to: '/projects', label: 'My Projects' },
    { to: '/profile', label: 'Profile' }
  ],
  beginner: [
    { to: '/', label: 'Home' },
    { to: '/academy', label: 'Academy' },
    { to: '/projects', label: 'Projects' },
    { to: '/portfolio', label: 'Portfolio' },
    { to: '/profile', label: 'Profile' }
  ],
  professional: [
    { to: '/', label: 'Home' },
    { to: '/find-projects', label: 'Find Projects' },
    { to: '/projects', label: 'My Projects' },
    { to: '/portfolio', label: 'Portfolio' },
    { to: '/profile', label: 'Profile' }
  ],
  company: [
    { to: '/', label: 'Home' },
    { to: '/find-talent', label: 'Find Talent' },
    { to: '/requirements', label: 'My Requirements' },
    { to: '/projects', label: 'Projects' },
    { to: '/profile', label: 'Company Profile' }
  ]
};
